import { useEffect, useLayoutEffect, useRef, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../lib/cx';
import type { MenuItem } from './MenuButton';
import styles from './MenuButton.module.css';

interface PointMenuProps {
  /** Accessible name of the menu. */
  readonly label: string;
  readonly items: readonly MenuItem[];
  /** Viewport coordinates of the menu's top-left corner (the pointer, or the opener's corner). */
  readonly x: number;
  readonly y: number;
  readonly testId?: string;
  /** Close; `restoreFocus` returns focus to the opener (Esc, Tab), not after an outside press or a choice. */
  readonly onClose: (restoreFocus: boolean) => void;
}

const EDGE_PX = 8;

/**
 * A menu opened at a point (right click, menu key) instead of from a button: the same panel and
 * keyboard model as `MenuButton` (arrow keys, Home/End, Enter or Space, Esc, Tab, press outside).
 * Rendered in a portal and clamped to the viewport. Choosing an item closes the menu first.
 */
export function PointMenu({ label, items, x, y, testId, onClose }: PointMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (menu === null) return;
    const { width, height } = menu.getBoundingClientRect();
    menu.style.left = `${Math.max(EDGE_PX, Math.min(x, window.innerWidth - width - EDGE_PX))}px`;
    menu.style.top = `${Math.max(EDGE_PX, Math.min(y, window.innerHeight - height - EDGE_PX))}px`;
    menu.style.visibility = 'visible';
    itemRefs.current[0]?.focus({ preventScroll: true });
  }, [x, y]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent): void => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target) === true) return;
      closeRef.current(false);
    };
    const onViewportChange = (): void => closeRef.current(false);
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, []);

  const focusItem = (index: number): void => {
    const count = items.length;
    itemRefs.current[((index % count) + count) % count]?.focus({ preventScroll: true });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const current = itemRefs.current.findIndex((element) => element === document.activeElement);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusItem(current + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusItem(current - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusItem(0);
        break;
      case 'End':
        event.preventDefault();
        focusItem(items.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        onClose(true);
        break;
      case 'Tab':
        event.preventDefault();
        onClose(true);
        break;
      default:
    }
  };

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={label}
      className={styles.menu}
      style={{ visibility: 'hidden' }}
      data-testid={testId}
      onKeyDown={onKeyDown}
      // A keyboard-opened menu can still receive a native right click: no browser menu on top of this one.
      onContextMenu={(event) => event.preventDefault()}
    >
      {items.map((item, index) => (
        <button
          key={item.id}
          ref={(element) => {
            itemRefs.current[index] = element;
          }}
          type="button"
          role="menuitem"
          tabIndex={-1}
          className={cx(styles.item, item.danger === true && styles.danger)}
          aria-disabled={item.disabled === true ? true : undefined}
          data-testid={item.testId}
          onClick={() => {
            if (item.disabled === true) return;
            onClose(false);
            item.onSelect();
          }}
        >
          {item.label}
        </button>
      ))}
    </div>,
    document.body,
  );
}
